import { AppContext } from "@/App.context";
import config from "@/config";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  API_LOADING_ERROR,
  API_NO_DATA_FOUND_FOR_DATE_RANGE_ERROR,
  API_NO_DATA_FOUND_FOR_FILTERS_ERROR,
  API_NOT_FOUND_ERROR,
  ERROR_STATUS_CODES
} from "@/lib/errors/error-constants";

/**
 * @typedef AllRECTableStatics
 * @property {Number} account
 * @property {String} resourceId
 * @property {String} currentType
 * @property {String} recommendedType
 * @property {Number} cost
 * @property {Number} savings
 * @property {Number} riSavings
 * @property {String} recommendationType
 * @property {String} region
 * @property {Number} utilization_cpu
 * @property {Number} utilization_mem
 * @property {String} owner
 */

/**
 * @typedef GetSavingFilters
 * @property {string[]} accounts
 * @property {string[]} regions
 * @property {string[]} tags
 */

/**
 * @typedef AllBodyStatisticsRes
 * @property {AllRECTableStatics} statistics
 * @property {GetSavingFilters} filters
 */

/**
 * @typedef GetStatisticsReq
 * @property {'aws' | 'azure' | 'gcp' | 'all-cloud' } service
 * @property {string} toDate
 * @property {string} fromDate
 * @property {number} page
 * @property {number} recordsPerPage
 */

export const getRectablestatics = (token) => {
  /**
   * @param {GetStatisticsReq} req
   * @returns {Promise<AllBodyStatisticsRes>}
   */
  return async (
    { service, fromDate, toDate, page, recordsPerPage, filters, desc, orderBy },
    signal
  ) => {
    const body = {
      accounts: filters?.accounts || [],
      regions: filters?.regions || [],
      tags: filters?.tags || [],
    };

    if (service === "azure") {
      body.rg = filters?.rg || [];
    }
    const owners = await fetch(
      `${config.apiHost}/v1/${service}/savings/recommendation?from=${fromDate}&to=${toDate}&page=${page}&recordsPerPage=${recordsPerPage}&desc=${desc}&orderBy=${orderBy}`,
      {
        method: "POST",
        body: JSON.stringify(body),
        signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );
    if (!owners.ok) {
      if (ERROR_STATUS_CODES.includes(owners.status)) {
        throw new Error(API_LOADING_ERROR);
      }
      if (owners.status === 404) {
        throw new Error(API_NOT_FOUND_ERROR);
      }
      throw new Error((await owners.json()).message);
    }

    const data = await owners.json();

    if (data?.data.length === 0) {
      if (body.accounts.length || body.regions.length || body.tags.length) {
        throw new Error(API_NO_DATA_FOUND_FOR_FILTERS_ERROR);
      }
      if (fromDate && toDate) {
        throw new Error(API_NO_DATA_FOUND_FOR_DATE_RANGE_ERROR);
      }
      throw new Error("No data available.");
    }

    return data;
  };
};

/**
 * @param {GetStatisticsReq} req
 * @param {any[]=} deps
 */
export const useStatisticstable = (req, deps = []) => {
  const appContext = useContext(AppContext);
  const query = useQuery({
    queryKey: ["getRectablestatics", appContext.idToken, req, ...deps],
    queryFn: () => getRectablestatics(appContext.idToken)(req),
  });
  return query;
};
